import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, Square, Circle, Check, X, Sparkles, Mic, Radio, Thermometer, Droplets, Wind, Shield, Zap, Users, Award } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import { Footer } from "@/components/Footer";
import { ProminentCTA } from "@/components/ui/prominent-cta";

// Product Images
import m3ProductImage from "@/assets/m3-hero-main.jpg";
import k2ProductImage from "@/assets/k2-lifestyle-luxury.jpg";
import s1ProductImage from "@/assets/s1-hero-egg.jpg";

export default function M3vsS1vsK2() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const comparisonSpecs = [
    { label: "Design Shape", m3: "Elongated One-Piece", k2: "Revolutionary Square", s1: "Patent Egg-Shaped", highlight: true },
    { label: "Control Method", m3: "Voice + Controller", k2: "Remote + Manual", s1: "One-key rotary knob", highlight: true },
    { label: "Auto Sensor", m3: "Radar sensor technology", k2: "Standard sensor", s1: "Standard sensor", highlight: true },
    { label: "Dimensions (L×W×H)", m3: "680mm × 380mm × 520mm", k2: "660mm × 400mm × 520mm", s1: "580mm × 430mm × 430mm", highlight: false },
    { label: "Product Weight", m3: "55kg", k2: "55kg", s1: "58kg", highlight: false },
    { label: "Water Tank", m3: "No pressure limit", k2: "Square design, no pressure limit", s1: "Built-in intelligent, no pressure limit", highlight: false },
    { label: "Flush System", m3: "Super spin siphonic", k2: "Super spin siphonic", s1: "Super spin siphonic with booster pump", highlight: false },
    { label: "Flush Volume", m3: "4L / 6L dual flush", k2: "4L / 6L dual flush", s1: "4L / 6L dual flush", highlight: false },
    { label: "Seat Design", m3: "Wider U-shaped ergonomic", k2: "Wide square seat ring", s1: "Ergonomic oval seat", highlight: true },
    { label: "Seat Heating", m3: "Thermostatic 36°C", k2: "4 levels adjustable", s1: "4 levels (Normal-40°C)", highlight: false },
    { label: "Water Heating", m3: "Instant tankless", k2: "Instant tankless heating", s1: "4 levels (Normal-40°C)", highlight: false },
    { label: "Air Dryer", m3: "Multi-level warm air", k2: "Warm air drying system", s1: "4 levels (Normal-55°C)", highlight: false },
    { label: "Voice Control", m3: "✓", k2: "—", s1: "—", highlight: true },
    { label: "Auto Open/Close", m3: "✓ Radar sensor", k2: "✓", s1: "✓", highlight: false },
    { label: "Auto Flush", m3: "✓", k2: "✓", s1: "✓", highlight: false },
    { label: "Foot Kick Sensor", m3: "✓ Seat ring control", k2: "—", s1: "✓ Opens seat ring", highlight: true },
    { label: "LED Display", m3: "✓", k2: "✓", s1: "✓ Large screen", highlight: false },
    { label: "Wash Modes", m3: "Female, Rear, Massage", k2: "Female, Rear, Massage", s1: "Female, Rear, Massage, Child", highlight: false },
    { label: "Smart Cleaning Bidet", m3: "✓", k2: "✓", s1: "✓", highlight: false },
    { label: "Deodorization", m3: "✓ Auto deodorization", k2: "✓", s1: "✓", highlight: false },
    { label: "Safety Protection", m3: "IPX4 Waterproof", k2: "IPX4 Waterproof", s1: "7-layer protection system", highlight: false },
    { label: "Power Consumption", m3: "1300W (max)", k2: "1300W (max)", s1: "1400W (max)", highlight: false },
    { label: "Warranty", m3: "3 Years Comprehensive", k2: "3 Years Comprehensive", s1: "3 Years Comprehensive", highlight: false },
    { label: "Price Range", m3: "Premium", k2: "Premium", s1: "Luxury", highlight: false }
  ];

  const m3Highlights = [
    { icon: <Mic className="w-6 h-6" />, text: "Voice control for hands-free operation" },
    { icon: <Radio className="w-6 h-6" />, text: "Radar sensor auto open/close technology" },
    { icon: <Zap className="w-6 h-6" />, text: "Foot kick seat ring control" },
    { icon: <Thermometer className="w-6 h-6" />, text: "Thermostatic seat heating at 36°C" },
    { icon: <Wind className="w-6 h-6" />, text: "Multi-level warm air drying system" }
  ];

  const k2Highlights = [
    { icon: <Square className="w-6 h-6" />, text: "Revolutionary square design breaks tradition" },
    { icon: <Sparkles className="w-6 h-6" />, text: "Wider square seat ring reduces fatigue" },
    { icon: <Zap className="w-6 h-6" />, text: "40mm shorter saves bathroom space" },
    { icon: <Thermometer className="w-6 h-6" />, text: "Two color options (White & Silver Grey)" }
  ];

  const s1Highlights = [
    { icon: <Circle className="w-6 h-6" />, text: "Award-winning iconic egg shape" },
    { icon: <Award className="w-6 h-6" />, text: "2022 MUSE Platinum Award Winner" },
    { icon: <Shield className="w-6 h-6" />, text: "7-layer safety protection system" },
    { icon: <Users className="w-6 h-6" />, text: "Child wash mode for families" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "ComparisonPage",
    "name": "M3 vs K2 vs S1 Smart Toilet Comparison",
    "description": "Compare the M3 Voice Control, K2 Square design, and S1 Egg-shaped smart toilets. Complete feature-by-feature comparison guide.",
    "mainEntity": [
      {
        "@type": "Product",
        "name": "M3 Smart Toilet",
        "description": "Voice control and radar sensor smart toilet with advanced technology"
      },
      {
        "@type": "Product",
        "name": "K2 Quadrate Smart Toilet",
        "description": "Revolutionary square design smart toilet with space-saving innovation"
      },
      {
        "@type": "Product",
        "name": "S1 Super Egg Smart Toilet",
        "description": "Award-winning egg-shaped smart toilet with 7-layer safety protection"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-50 text-foreground">
      <SEO
        title="M3 vs K2 vs S1 | Smart Toilet Comparison 2025 - AVT"
        description="Compare M3 Voice Control, K2 Square design, and S1 Egg-shaped smart toilets. Feature-by-feature comparison of technology, design, and specifications."
        keywords="M3 vs K2 vs S1, smart toilet comparison, voice control toilet, square toilet, egg toilet, toilet comparison 2025"
        url="/compare/m3-vs-s1-vs-k2"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "M3 vs K2 vs S1 Comparison", url: "/compare/m3-vs-s1-vs-k2" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 fade-in-observer">
            <Badge variant="outline" className="border-primary/50 text-primary bg-primary/5 mb-4">
              Product Comparison
            </Badge>
            <h1 className="font-playfair text-4xl md:text-6xl font-light text-primary mb-4">
              M3 <span className="text-muted-foreground">vs</span> K2 <span className="text-muted-foreground">vs</span> S1
            </h1>
            <p className="font-inter text-xl text-muted-foreground max-w-3xl mx-auto">
              Three innovative smart toilets with unique strengths. Compare voice control, square design, and egg-shaped technology.
            </p>
          </div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16 fade-in-observer">
            {/* M3 Card */}
            <Card className="overflow-hidden shadow-luxury border-blue-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={m3ProductImage}
                  alt="M3 smart toilet with voice control and radar sensor technology"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-blue-600 text-white">
                  Voice Control
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">M3 SMART</h2>
                <p className="text-muted-foreground mb-4 text-sm">Voice control & radar sensor technology</p>
                <div className="space-y-2">
                  {m3Highlights.slice(0, 4).map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-blue-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                  <LocalizedLink to="/products/m3">View M3 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>

            {/* K2 Card */}
            <Card className="overflow-hidden shadow-luxury border-amber-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={k2ProductImage}
                  alt="K2 Quadrate smart toilet with revolutionary square design"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-amber-600 text-white">
                  Square Design
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">K2 QUADRATE</h2>
                <p className="text-muted-foreground mb-4 text-sm">Revolutionary square space-saving design</p>
                <div className="space-y-2">
                  {k2Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-amber-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-amber-600 hover:bg-amber-700">
                  <LocalizedLink to="/products/k2">View K2 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>

            {/* S1 Card */}
            <Card className="overflow-hidden shadow-luxury border-teal-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={s1ProductImage}
                  alt="S1 Super Egg smart toilet with award-winning spherical design"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-teal-600 text-white">
                  Egg Design
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">S1 SUPER EGG</h2>
                <p className="text-muted-foreground mb-4 text-sm">Award-winning egg shape with safety features</p>
                <div className="space-y-2">
                  {s1Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-teal-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-teal-600 hover:bg-teal-700">
                  <LocalizedLink to="/products/s1">View S1 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-6 lg:px-12 bg-white/80 backdrop-blur-sm fade-in-observer">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Feature Comparison</h2>
          
          <div className="bg-white rounded-2xl shadow-luxury overflow-hidden overflow-x-auto">
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground min-w-[600px]">
              <div className="p-4 font-medium text-sm">Feature</div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Mic className="w-4 h-4" /> M3 Smart
                </span>
              </div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Square className="w-4 h-4" /> K2 Quadrate
                </span>
              </div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Circle className="w-4 h-4" /> S1 Super Egg
                </span>
              </div>
            </div>

            {/* Table Body */}
            {comparisonSpecs.map((spec, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-4 border-b border-stone-100 min-w-[600px] ${
                  spec.highlight ? 'bg-gradient-to-r from-blue-50/50 via-amber-50/50 to-teal-50/50' : 
                  index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                }`}
              >
                <div className="p-4 text-sm font-medium text-muted-foreground flex items-center">
                  {spec.highlight && <Sparkles className="w-3 h-3 text-accent mr-2" />}
                  {spec.label}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.m3 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.m3 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.m3}</span>
                  )}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.k2 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.k2 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.k2}</span>
                  )}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.s1 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.s1 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.s1}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-16 px-6 lg:px-12 fade-in-observer">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Which One is Right for You?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* M3 Best For */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Mic className="w-8 h-8 text-blue-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose M3 If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want voice control for hands-free use</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You prefer radar sensor auto detection</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want foot kick seat ring control</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You need wider U-shaped ergonomic seat</span>
                </li>
              </ul>
            </div>

            {/* K2 Best For */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Square className="w-8 h-8 text-amber-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose K2 If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want a bold square design statement</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Your bathroom has limited space</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You prefer wider square seat ring</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You need wall drain installation</span>
                </li>
              </ul>
            </div>

            {/* S1 Best For */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Circle className="w-8 h-8 text-teal-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose S1 If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want an award-winning iconic design</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Safety is your top priority</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You have a family with children</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You prefer simple one-knob control</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ProminentCTA 
        title="Need Help Deciding?"
        description="Our team can help you choose the perfect smart toilet for your bathroom. Contact us for personalized recommendations."
        primaryCTA={{ text: "Request Consultation", href: "/contact" }}
        secondaryCTA={{ text: "View All Products", href: "/smart-toilets" }}
        showContactButtons={true}
      />

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all z-50"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <Footer />
    </div>
  );
}
